/**
 * Main application entry point
 * Validates Requirements 3.4.2, 4.2.1
 */

import { VideoStream } from './modules/pose-detection/VideoStream';
import { PoseDetector } from './modules/pose-detection/PoseDetector';
import { AnimationEngine } from './modules/animation-engine/AnimationEngine';
import { StateManager } from './modules/state-management/StateManager';
import { GestureRecognizer } from './modules/gesture-recognition/GestureRecognizer';
import { PersonTracker } from './modules/person-tracking/PersonTracker';
import type { AppState, PoseData, MultiPersonConfig } from './types';
import { cycleVisualizationType } from './types';
import { PerformanceMonitor } from './utils/PerformanceMonitor';
import { logger } from './utils/logger';

/**
 * Default configuration for multi-person tracking.
 * Soft limit of 2 people for MVP, extensible to 4+ without major refactoring.
 * Validates Requirements 10.5
 */
const DEFAULT_MULTI_PERSON_CONFIG: MultiPersonConfig = {
  maxPeople: 2,
  matchingThreshold: 0.15,
  timeoutFrames: 30,
};

/**
 * Main application class that coordinates all modules
 */
class InteractiveMotionArt {
  private videoStream: VideoStream | null = null;
  private poseDetector: PoseDetector | null = null;
  private animationEngine: AnimationEngine | null = null;
  private stateManager: StateManager;
  private gestureRecognizer: GestureRecognizer;
  private personTracker: PersonTracker;
  private performanceMonitor: PerformanceMonitor;
  private isInitialized = false;
  private frameCounter = 0;
  private previousPeopleIds: Set<string> = new Set();
  private config: MultiPersonConfig;

  constructor(config?: Partial<MultiPersonConfig>) {
    // Merge provided config with defaults
    // Validates Requirements 10.5
    this.config = {
      ...DEFAULT_MULTI_PERSON_CONFIG,
      ...config,
    };
    
    // Initialize state manager with default state
    this.stateManager = new StateManager();
    
    // Initialize gesture recognizer
    this.gestureRecognizer = new GestureRecognizer();
    
    // Initialize person tracker with multi-person configuration
    // Validates Requirements 1.2, 2.5, 2.3, 10.5
    this.personTracker = new PersonTracker(this.config);
    
    // Initialize performance monitor
    this.performanceMonitor = new PerformanceMonitor();
    
    // Expose globally for testing
    if (typeof window !== 'undefined') {
      (window as any).performanceMonitor = this.performanceMonitor;
      (window as any).gestureRecognizer = this.gestureRecognizer;
    }
  }

  /**
   * Get current multi-person configuration.
   * @returns Current configuration
   */
  getConfig(): MultiPersonConfig {
    return { ...this.config };
  }

  /**
   * Initialize all modules and set up data flow
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Check WebGL availability
      this.checkWebGLSupport();

      // Show loading indicator
      this.showLoading('Initializing camera...');

      // Initialize video stream
      this.videoStream = new VideoStream();
      await this.videoStream.initialize();

      // Initialize pose detector
      this.showLoading('Loading pose detection model...');
      this.poseDetector = new PoseDetector(this.videoStream);
      await this.poseDetector.initialize({ numPoses: this.config.maxPeople }); // Configure for multi-person detection using config

      // Initialize animation engine
      const canvas = document.getElementById('canvas') as HTMLCanvasElement;
      if (!canvas) {
        throw new Error('Canvas element not found');
      }

      this.animationEngine = new AnimationEngine();
      await this.animationEngine.initialize(canvas);

      // Pass video element reference to animation engine for camera preview
      const videoElement = this.videoStream.getVideoElement();
      this.animationEngine.setVideoElement(videoElement);

      // Set up mode toggle callback
      this.animationEngine.setModeToggleCallback(() => {
        this.stateManager.toggleDisplayMode();
      });

      // Set up debug toggle callback
      // Validates Requirements 3.2.23, 3.4.7
      this.animationEngine.setDebugToggleCallback(() => {
        this.stateManager.toggleDebugOverlay();
      });

      // Set up per-person visualization selector callback (manual switching).
      // Cycles the clicked person's visualization type in the given direction.
      this.animationEngine.setVisualizationSelectCallback((personId: string, direction: 1 | -1) => {
        const person = this.stateManager.getState().people.people.get(personId);
        if (person) {
          const nextType = cycleVisualizationType(person.visualizationType, direction);
          this.stateManager.setPersonVisualizationType(personId, nextType);
        }
      });

      // Subscribe to state changes and update animation engine
      this.stateManager.subscribe((state: AppState) => {
        if (this.animationEngine) {
          this.animationEngine.render(state);
        }
      });

      // Wire up multi-person pose updates: PoseDetector → PersonTracker → GestureRecognizer → StateManager → AnimationEngine
      // Validates Requirements 1.1, 1.5, 4.4
      this.poseDetector.onPosesUpdate((poses: PoseData[]) => {
        try {
          // Record frame capture for latency measurement
          const frameId = this.frameCounter++;
          this.performanceMonitor.recordFrameCapture(frameId);
          
          // Update person tracking with detected poses
          const personCollection = this.personTracker.update(poses, frameId);
          
          // Record pose frame for performance monitoring
          this.performanceMonitor.recordPoseFrame(frameId);
          
          // Detect people who have exited (were in previous frame but not in current)
          // Validates Requirements 1.4, 5.3, 5.4
          const currentPeopleIds = new Set(personCollection.people.keys());
          const removedPeopleIds = Array.from(this.previousPeopleIds).filter(
            id => !currentPeopleIds.has(id)
          );
          
          // Clean up state for removed people
          removedPeopleIds.forEach(personId => {
            // Reset gesture state for this person
            this.gestureRecognizer.resetPerson(personId);
            
            // Release the AnimationEngine's per-person resources (trail buffers,
            // motion tracker, and artistic renderer instance) so these maps don't
            // grow unbounded as people enter and leave the frame.
            this.animationEngine?.cleanupPerson(personId);
            
            logger.log(`[Main] Cleaned up state for removed person: ${personId}`);
          });
          
          // Update tracked people IDs for next frame
          this.previousPeopleIds = currentPeopleIds;
          
          // Process gestures for each person
          // Validates Requirements 4.1, 4.2, 4.3
          personCollection.people.forEach((person) => {
            const { personId, poseData } = person;
            
            // Only process gestures if pose is detected
            if (poseData && poseData.detected) {
              // Detect all currently active gestures for this person (no debouncing)
              const activeGestureTypes = this.gestureRecognizer.detectActiveGestures(poseData, personId);
              
              // Update person's gesture state in the collection
              person.gestureState.activeGestures = new Set(activeGestureTypes);
              
              // Analyze pose for debounced gesture actions (color change, mode toggle, etc.)
              const detectedGestures = this.gestureRecognizer.analyzePose(poseData, personId);
              
              // Handle each detected gesture action for this person
              detectedGestures.forEach(gesture => {
                this.stateManager.handleGesture(gesture, personId);
              });
            } else {
              // Clear active gestures when no pose detected for this person
              person.gestureState.activeGestures = new Set();
            }
          });
          
          // Update state with the (gesture-annotated) person collection
          this.stateManager.updatePeople(personCollection);
        } catch (error) {
          console.error('Error processing pose update:', error);
          // Continue execution - don't let gesture recognition errors break the app
        }
      });

      // Start pose detection
      this.poseDetector.start();
      
      // Start performance monitoring
      this.performanceMonitor.start();

      // Hide loading indicator
      this.hideLoading();

      this.isInitialized = true;
      logger.log('Interactive Motion Art initialized successfully');
      logger.log('Performance monitoring enabled. Access via window.performanceMonitor');
    } catch (error) {
      this.handleInitializationError(error);
      throw error;
    }
  }

  /**
   * Check if WebGL is supported
   */
  private checkWebGLSupport(): void {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) {
      throw new Error('WebGL is not available. Please use a browser that supports WebGL (Chrome 90+, Firefox 88+).');
    }
  }

  /**
   * Clean up resources on application shutdown
   */
  cleanup(): void {
    // Stop performance monitoring
    if (this.performanceMonitor) {
      this.performanceMonitor.stop();
    }

    if (this.poseDetector) {
      this.poseDetector.cleanup();
      this.poseDetector = null;
    }

    if (this.videoStream) {
      this.videoStream.cleanup();
      this.videoStream = null;
    }

    if (this.animationEngine) {
      this.animationEngine.dispose();
      this.animationEngine = null;
    }

    this.isInitialized = false;
  }

  /**
   * Show loading indicator
   */
  private showLoading(message: string): void {
    const loadingElement = document.getElementById('loading');
    const loadingText = document.getElementById('loading-text');
    
    if (loadingElement) {
      loadingElement.style.display = 'flex';
    }
    
    if (loadingText) {
      loadingText.textContent = message;
    }
  }

  /**
   * Hide loading indicator
   */
  private hideLoading(): void {
    const loadingElement = document.getElementById('loading');
    if (loadingElement) {
      loadingElement.style.display = 'none';
    }
  }

  /**
   * Handle initialization errors
   */
  private handleInitializationError(error: unknown): void {
    console.error('Initialization error:', error);
    
    const errorElement = document.getElementById('error');
    const errorMessage = document.getElementById('error-message');
    
    if (errorElement && errorMessage) {
      errorElement.style.display = 'flex';
      
      if (error instanceof Error) {
        errorMessage.textContent = error.message;
      } else {
        errorMessage.textContent = 'An unknown error occurred during initialization';
      }
    }
    
    this.hideLoading();
  }
}

// Global application instance
let app: InteractiveMotionArt | null = null;

/**
 * Initialize application on DOMContentLoaded
 */
document.addEventListener('DOMContentLoaded', async () => {
  try {
    app = new InteractiveMotionArt();
    await app.initialize();
  } catch (error) {
    console.error('Failed to initialize application:', error);
  }
});

/**
 * Cleanup on page unload
 */
window.addEventListener('beforeunload', () => {
  if (app) {
    app.cleanup();
  }
});

/**
 * Global error boundary for uncaught exceptions
 */
window.addEventListener('error', (event) => {
  console.error('Uncaught error:', event.error);
  
  const errorElement = document.getElementById('error');
  const errorMessage = document.getElementById('error-message');
  
  if (errorElement && errorMessage) {
    errorElement.style.display = 'flex';
    errorMessage.textContent = `An unexpected error occurred: ${event.error?.message || 'Unknown error'}`;
  }
});

/**
 * Global error boundary for unhandled promise rejections
 */
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  
  const errorElement = document.getElementById('error');
  const errorMessage = document.getElementById('error-message');
  
  if (errorElement && errorMessage) {
    errorElement.style.display = 'flex';
    errorMessage.textContent = `An unexpected error occurred: ${event.reason?.message || event.reason || 'Unknown error'}`;
  }
});
